// src/pages/users/AddUser.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import { API_BASE_URL, getAuthHeaders } from './auth';

const AddUser = () => {
  // Initial form data for the new user.
  // "customerName" will hold the selected Customer _id.
  const initialFormData = {
    firstName: '',
    lastName: '',
    customerName: '',
    email: '',
    password: '',
    role: 'customer',
  };

  const [formData, setFormData] = useState(initialFormData);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // State for customer dropdown options
  const [customers, setCustomers] = useState([]);
  const [dropdownLoading, setDropdownLoading] = useState(true);

  // Fetch customers data for the dropdown
  useEffect(() => {
    fetch(`${API_BASE_URL}/api/customers`, { headers: getAuthHeaders() })
      .then((res) => {
        if (!res.ok) {
          throw new Error('Error fetching customers');
        }
        return res.json();
      })
      .then((data) => {
        setCustomers(data);
        setDropdownLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setDropdownLoading(false);
      });
  }, []);

  // Handle changes for text fields and dropdowns.
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle form submission (POST request)
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/register`, {
        method: 'POST',
        headers: getAuthHeaders(true),
        body: JSON.stringify(formData),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Registration failed');
      }
      setSuccess('User registered successfully!');
      setFormData(initialFormData);
    } catch (err) {
      setError(err.message);
    }
    setSubmitting(false);
  };

  return (
    <Box maxWidth="400px" mx="auto" p={2}>
      <Typography variant="h4" align="center" gutterBottom>
        Add User
      </Typography>
      {error && (
        <Typography color="error" align="center" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}
      {success && (
        <Typography color="primary" align="center" sx={{ mb: 2 }}>
          {success}
        </Typography>
      )}
      <Box component="form" onSubmit={handleSubmit} noValidate autoComplete="off">
        <TextField
          label="First Name"
          name="firstName"
          value={formData.firstName}
          onChange={handleChange}
          fullWidth
          margin="normal"
        />
        <TextField
          label="Last Name"
          name="lastName"
          value={formData.lastName}
          onChange={handleChange}
          fullWidth
          margin="normal"
        />
        {dropdownLoading ? (
          <Box display="flex" justifyContent="center" my={2}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <FormControl fullWidth margin="normal">
            <InputLabel id="customerName-label">Customer</InputLabel>
            <Select
              labelId="customerName-label"
              id="customerName"
              name="customerName"
              value={formData.customerName}
              onChange={handleChange}
              label="Customer"
            >
              {customers.map((customer) => (
                <MenuItem key={customer._id} value={customer._id}>
                  {customer.customerName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <TextField
          label="Email"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleChange}
          fullWidth
          margin="normal"
        />
        <TextField
          label="Password"
          name="password"
          type="password"
          value={formData.password}
          onChange={handleChange}
          fullWidth
          margin="normal"
        />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          New users created under a customer are assigned the Customer role.
        </Typography>
        <Box mt={2}>
          <Button type="submit" variant="contained" color="primary" fullWidth disabled={submitting}>
            {submitting ? 'Submitting...' : 'Register'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default AddUser;
